import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { ApiError } from "./api";

/** Settings save as you go: each change is sent after a short pause, the last one wins, a failure is said in a toast and the field
 *  keeps what the person typed so they can try again. In plain words: no Save button; what you see is what is stored, a moment later.
 *
 *  2026-09-29: the sender and the error handler live in refs, so the pause is real. Before, an inline `send` made a new flush on
 *  every render, and the unmount cleanup keyed on it fired the pending save on every keystroke: one request per key, and the
 *  answer's reload put the older value back over what was typed meanwhile. */
/** A form field's state that takes the server's value once, and again only when `key` names another record (another project,
 *  another person); never after the form's own save, whose reload would put the sent value back over keys typed since (2026-10-01,
 *  the founder's second report: the sign-off and the signature). While the form is open it is the source of truth. */
export function useFormState<T>(initial: T, key: string): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState(initial);
  const seen = useRef(key);
  useEffect(() => { if (seen.current !== key) { seen.current = key; setState(initial); } }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return [state, setState];
}

const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
/** two patches waiting for one send, merged one level deeper (the signature HTML and role round, 2026-10-28): `{ names: { customerName } }`
 *  then `{ names: { customerRole } }` sends both names, not only the second */
export function mergePatch<P extends object>(pending: P | null, patch: P): P {
  const out: Record<string, unknown> = { ...(pending ?? {}) };
  for (const [k, v] of Object.entries(patch)) out[k] = isPlainObject(out[k]) && isPlainObject(v) ? { ...out[k], ...v } : v;
  return out as P;
}

export function useSaver<P extends object>(send: (patch: P) => Promise<unknown>, onError: (message: string) => void, delay = 400) {
  const sendRef = useRef(send); sendRef.current = send;
  const onErrorRef = useRef(onError); onErrorRef.current = onError;
  const pending = useRef<P | null>(null); const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saving, setSaving] = useState(false);
  const flush = useCallback(async () => {
    const p = pending.current; pending.current = null; timer.current = null;
    if (!p) return;
    setSaving(true);
    try { await sendRef.current(p); } catch (e) { onErrorRef.current(e instanceof ApiError ? e.message : "Could not save"); } finally { setSaving(false); }
  }, []);
  const save = useCallback((patch: P) => { pending.current = mergePatch(pending.current, patch); if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => void flush(), delay); }, [flush, delay]);
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); void flush(); } }, [flush]); // on unmount only: flush is stable
  return { save, saving };
}
